import { Disclosure } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import cn from "classnames";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useCartWithHooks } from "~/hooks/use-cart";

import Icon from "../common/icon";
import InputField from "../form/input-field";
import styles from "./cart-promo-code.module.scss";

export default function CartPromoCode() {
  const t = useTranslations();
  const { cart, addCouponCode, cancelDiscountOrderLine, couponError, setCouponError } = useCartWithHooks();
  const schema = z.object({ code: z.string().min(1, t("error.fieldRequired")) });
  const form = useForm<Fields>({ resolver: zodResolver(schema) });
  type Fields = z.infer<typeof schema>;
  const formRef = useRef<HTMLFormElement>(null);
  const [infoMessage, setInfoMessage] = useState<string>();

  const currentCoupon = cart?.ShoppingCart.Lines.find((line) => line.DiscountCouponCode);

  useEffect(() => {
    if (currentCoupon?.ID) {
      form.clearErrors("code");
      setInfoMessage(undefined);
    }
  }, [currentCoupon, form]);

  const watchPromoCode = form.watch("code");

  // handle async error from AddDiscountCouponToOrder
  useEffect(() => {
    if (couponError) {
      switch (couponError.Type) {
        case "Discounts:InvalidCoupon":
          form.setError("code", { message: t("error.invalid_coupon", { code: watchPromoCode }) });
          break;

        case "Discounts:CouponAlreadyAdded":
          form.setError("code", { message: t("error.coupon_already_added") });
          break;

        default:
          form.setError("code", { message: t("error.invalid_coupon", { code: watchPromoCode }) });
          break;
      }
      setCouponError(undefined);
      (formRef.current?.querySelector("[aria-invalid]") as HTMLElement)?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponError, watchPromoCode, setCouponError]);

  async function onSubmit(fields: Fields) {
    setInfoMessage(undefined);
    if (currentCoupon && currentCoupon.ID) {
      return cancelDiscountOrderLine({
        payload: {
          OrderLineID: currentCoupon.ID,
        },
      });
    } else {
      const couponResponse = await addCouponCode({
        orderType: 0,
        payload: {
          CouponCode: fields.code,
        },
      });

      if (couponResponse && !couponResponse.DiscountIsApplied) {
        setInfoMessage(t("error.invalid_coupon_combination"));
      }
    }
  }

  return (
    <div className={styles.container}>
      <Disclosure as="div">
        {({ open }) => (
          <>
            <Disclosure.Button className={cn(styles.button, open && styles.openButton)}>
              {t("generic.cart.haveAPromoCode")}
              <Icon name={open ? "chevron-up" : "chevron-down"} aria-hidden width={16} height={16} />
            </Disclosure.Button>
            <Disclosure.Panel static className={styles.panel}>
              <form ref={formRef} className={styles.panelInner} onSubmit={form.handleSubmit(onSubmit)}>
                <InputField
                  label={t("generic.promoCode")}
                  disabled={form.formState.isSubmitting}
                  readOnly={!!currentCoupon}
                  {...form.register("code", { required: true })}
                  defaultValue={currentCoupon?.DiscountCouponCode}
                  buttonName={currentCoupon ? t("generic.remove") : t("generic.apply")}
                  hideErrorIcon
                  error={form.formState.errors.code?.message}
                  informativeMessage={infoMessage}
                  successMessage={currentCoupon ? t("generic.codeApplied") : undefined}
                  clearValue={() => {
                    form.reset();
                    setInfoMessage(undefined);
                  }}
                />
              </form>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
}
